import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ShieldCheckIcon, ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';

const registerSchema = z
  .object({
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    phoneNumber: z
      .string()
      .regex(/^\+27[0-9]{9}$/, 'Phone number must start with +27 followed by 9 digits (e.g., +27845533264)'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .refine(
        (val) => {
          const cats = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/];
          return cats.filter((r) => r.test(val)).length >= 3;
        },
        'Password must meet at least 3 of: lowercase, uppercase, number, special character'
      )
      .refine(
        (val) => !/(.)\1{2,}/.test(val),
        'No more than 2 identical characters in a row'
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

function RequirementItem({ met, label }: { met: boolean; label: string }) {
  return (
    <li className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met ? (
        <CheckIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
      ) : (
        <span className="h-4 w-4 mr-1.5 flex-shrink-0 flex items-center justify-center">
          <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        </span>
      )}
      {label}
    </li>
  );
}

export function RegisterPage() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // Live password validation for requirements checklist
  const passwordValue = watch('password') || '';
  const passwordChecks = {
    minLength: passwordValue.length >= 8,
    lowercase: /[a-z]/.test(passwordValue),
    uppercase: /[A-Z]/.test(passwordValue),
    number: /[0-9]/.test(passwordValue),
    special: /[^A-Za-z0-9]/.test(passwordValue),
    noRepeat: passwordValue.length === 0 || !/(.)\1{2,}/.test(passwordValue),
  };
  const categoryCount = [
    passwordChecks.lowercase,
    passwordChecks.uppercase,
    passwordChecks.number,
    passwordChecks.special,
  ].filter(Boolean).length;
  const atLeast3Categories = categoryCount >= 3;

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      await authApi.register(data);
      setSuccess(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          <div className="text-center space-y-4">
            <ShieldCheckIcon className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-3xl font-bold text-gray-900">Check your email</h2>
            <p className="text-gray-600">
              We've sent a verification link to your email address. Please click the link to verify
              your account before signing in.
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
        </div>
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2">
            <ShieldCheckIcon className="h-12 w-12 text-primary-600" />
          </Link>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Create your account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && <Alert type="error" message={error} />}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="label">
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className={`input ${errors.firstName ? 'input-error' : ''}`}
                  {...register('firstName')}
                />
                {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="label">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className={`input ${errors.lastName ? 'input-error' : ''}`}
                  {...register('lastName')}
                />
                {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                {...register('email')}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="phoneNumber" className="label">
                Phone number
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  +27
                </span>
                <input
                  id="phoneNumber"
                  type="tel"
                  autoComplete="tel"
                  placeholder="845533264"
                  className={`input rounded-l-none ${errors.phoneNumber ? 'input-error' : ''}`}
                  {...register('phoneNumber', {
                    onChange: (e) => {
                      let val = e.target.value.replace(/[^0-9+]/g, '');
                      // Auto-prepend +27 if user types digits without it
                      if (val && !val.startsWith('+')) {
                        // Strip leading 0 if present
                        if (val.startsWith('0')) val = val.substring(1);
                        // Strip leading 27 if present (user typed country code without +)
                        if (val.startsWith('27') && val.length > 9) val = val.substring(2);
                        val = '+27' + val;
                      }
                      e.target.value = val;
                    },
                  })}
                />
              </div>
              {errors.phoneNumber && <p className="error-text">{errors.phoneNumber.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                className={`input ${errors.password ? 'input-error' : ''}`}
                {...register('password')}
              />

              {/* Live password requirements checklist */}
              <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Your password must contain:</p>
                <ul className="space-y-1">
                  <RequirementItem met={passwordChecks.minLength} label="At least 8 characters" />
                  <RequirementItem met={atLeast3Categories} label="At least 3 of the following:" />
                  <ul className="ml-5 space-y-1">
                    <RequirementItem met={passwordChecks.lowercase} label="Lower case letters (a-z)" />
                    <RequirementItem met={passwordChecks.uppercase} label="Upper case letters (A-Z)" />
                    <RequirementItem met={passwordChecks.number} label="Numbers (0-9)" />
                    <RequirementItem met={passwordChecks.special} label="Special characters (e.g. !@#$%^&*)" />
                  </ul>
                  <RequirementItem met={passwordChecks.noRepeat} label="No more than 2 identical characters in a row" />
                </ul>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="error-text">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <button type="submit" className="btn-primary w-full py-3" disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" /> : 'Create account'}
          </button>

          <p className="text-xs text-gray-500 text-center">
            By creating an account, you agree to our{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Privacy Policy
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
